import React from "react";
import { ModuleConfig } from "../../types/ModuleConfig";

// Import project components (lazy-loaded for performance)
const ProjectsList = React.lazy(() => import("./ProjectsList"));
const NewProject = React.lazy(() => import("./new"));
const ProjectPage = React.lazy(() => import("./ProjectPage"));
const TimelineView = React.lazy(() => import("./TimelineView"));

const projectsModuleConfig: ModuleConfig = {
    // 🏷️ MODULE IDENTITY
    id: "projects",
    name: "Project Management",
    description: "Track projects, tasks, and deadlines",
    version: "1.0.6",
    enabled: true,
    category: "productivity",
    order: 5,
    icon: "📊",
    color: "#805AD5",

    // 🛣️ ROUTES
    routes: [
        {
            path: "/projects",
            component: ProjectsList,
            permissions: ["PROJECTS_ADMIN", "ADMIN", "MANAGER"]
        },
        {
            path: "/projects/new",
            component: NewProject,
            permissions: ["PROJECTS_ADMIN", "ADMIN", "MANAGER"]
        },
        {
            path: "/project/:id",
            component: ProjectPage,
            permissions: []  // Empty array = public access
        },
        {
            path: "/project/:id/timeline",
            component: TimelineView,
            permissions: ["PROJECTS_ADMIN", "USER", "ADMIN", "MANAGER"]
        }
    ],

    // 🧭 NAVIGATION
    navigation: [
        {
            label: "All Projects",
            path: "/projects",
            icon: "📋",
            permissions: ["PROJECTS_ADMIN", "ADMIN", "MANAGER"]
        },
        {
            label: "New Project",
            path: "/projects/new",
            icon: "➕",
            permissions: ["PROJECTS_ADMIN", "ADMIN", "MANAGER"]
        }
    ],

    // 🔐 PERMISSIONS
    permissions: {
        view: ["PROJECTS_ADMIN", "USER", "ADMIN", "MANAGER"],
        create: ["PROJECTS_ADMIN", "ADMIN", "MANAGER"],
        edit: ["PROJECTS_ADMIN", "ADMIN", "MANAGER"],
        delete: ["PROJECTS_ADMIN", "ADMIN"]
    },

    // ⚡ QUICK ACTIONS
    quickActions: [
        {
            label: "New Project",
            path: "/projects/new",
            icon: "➕",
            description: "Start a new project"
        }
    ],

    // 📝 CHANGELOG
    changelog: [
        {
            version: "1.0.6",
            releaseDate: "2025-11-22",
            title: "Bug Fix - Import/Export Visibility",
            features: [
                "🐛 Fixed import/export buttons not showing when project has no tasks",
                "🐛 Fixed 'Add Your First Task' banner showing when tasks exist"
            ],
            notes: "Import and export buttons now visible for empty projects, allowing markdown import from the start."
        },
        {
            version: "1.0.5",
            releaseDate: "2025-11-22",
            title: "Stable Release - Production Ready",
            features: [
                "✅ Production-ready project management system",
                "✅ Complete task lifecycle management",
                "✅ Evidence timeline with media support",
                "✅ AI-enhanced descriptions and goals",
                "✅ Full TypeScript type safety"
            ],
            notes: "Official stable release with comprehensive project tracking and team collaboration features."
        },
        {
            version: "0.8.0",
            releaseDate: "2025-10-18",
            title: "Enhanced Task Management",
            features: [
                "✨ Added WattLab markdown template (139 lines)",
                "✨ Implemented task append functionality (119 lines)",
                "✨ Enhanced project templates for common use cases",
                "✨ Improved task organization and workflow"
            ],
            notes: "Task management enhancements with industry-specific templates."
        },
        {
            version: "0.7.0",
            releaseDate: "2025-10-17",
            title: "Markdown Templates System",
            features: [
                "✨ Created centralized markdown templates (295 lines)",
                "✨ Added project spec templates",
                "✨ Implemented template import/export",
                "✨ Enhanced documentation capabilities"
            ],
            notes: "Comprehensive markdown template system for project documentation."
        },
        {
            version: "0.6.0",
            releaseDate: "2025-10-08",
            title: "Bills & Projects Integration",
            features: [
                "✨ Added bills and projects spec generation (272 lines)",
                "✨ Enhanced project billing integration",
                "✨ Improved project documentation workflows",
                "✨ Added specification templates"
            ],
            notes: "Integration with billing system for project monetization."
        },
        {
            version: "0.5.0",
            releaseDate: "2024-09-20",
            title: "Theme System Migration",
            features: [
                "✨ Full light/dark mode support across all project pages",
                "✨ Enhanced ProjectsList with theme-aware styling",
                "✨ Updated color mode consistency",
                "✨ Improved visual design system"
            ],
            notes: "Complete theme system implementation for better user experience."
        },
        {
            version: "0.4.0",
            releaseDate: "2024-09-13",
            title: "GraphQL Code Generation",
            features: [
                "✨ Added GraphQL codegen support (38 deletions, 23 insertions)",
                "✨ Fixed nullable field handling in TaskModal",
                "✨ Enhanced TypeScript type safety",
                "✨ Improved API integration"
            ],
            notes: "GraphQL integration improvements for better type safety."
        },
        {
            version: "0.3.0",
            releaseDate: "2024-08-12",
            title: "UI Consistency & Branding",
            features: [
                "✨ Major UI consistency overhaul (470 insertions, 148 deletions)",
                "✨ Enhanced brand configuration integration",
                "✨ Added task completion rewards (147 lines)",
                "✨ Improved timeline view with branding (140 insertions)",
                "✨ Enhanced AddMemberModal styling"
            ],
            notes: "Comprehensive UI improvements and brand consistency updates."
        },
        {
            version: "0.2.0",
            releaseDate: "2024-06-17",
            title: "Feature Expansion & UI Refactor",
            features: [
                "🔨 Major component refactoring (1,314 insertions, 1,136 deletions)",
                "✨ Enhanced subscription management integration",
                "✨ Improved routing capabilities",
                "✨ Better UI consistency across components",
                "✨ Performance optimizations"
            ],
            notes: "Major refactoring effort improving maintainability and performance."
        },
        {
            version: "0.1.0",
            releaseDate: "2024-06-14",
            title: "Initial Project Management System",
            features: [
                "✅ Created ProjectPage component (1,311 lines)",
                "✅ Created ProjectsList component (314 lines)",
                "✅ Created TimelineView component (534 lines)",
                "✅ Created NewProject component (317 lines)",
                "✅ Basic CRUD operations for projects and tasks",
                "✅ Initial GraphQL integration",
                "✅ Team member management",
                "✅ Evidence upload with media support"
            ],
            notes: "Initial implementation of the project management system with 2,515 lines of code."
        }
    ],

    // 🗺️ ROADMAP
    roadmap: []

};

export default projectsModuleConfig;